import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "./AuthContext";
import "./Login.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/auth`
  : "/api/auth";

const EST_API = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/establishment`
  : "/api/establishment";

const Login = () => {
  const [stage, setStage] = useState("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [establishmentId, setEstablishmentId] = useState("");
  const [establishments, setEstablishments] = useState([]);
  const [estLoading, setEstLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { login } = useAuth();

  // Fetch establishments when switching to register
  useEffect(() => {
    if (stage === "register" && establishments.length === 0) {
      setEstLoading(true);
      fetch(`${EST_API}/public`)
        .then(r => r.json())
        .then(d => setEstablishments(d.establishments || []))
        .catch(() => setEstablishments([]))
        .finally(() => setEstLoading(false));
    }
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- LOGIN ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    if ((!username && !email) || !password) {
      setError("Please enter username/email and password");
      setIsLoading(false);
      return;
    }
    try {
      const loginId = username || email;
      const result = await login(loginId, password);
      if (!result.success) setError(result.message || "Login failed");
    } catch {
      setError("Unexpected error during login");
    } finally {
      setIsLoading(false);
    }
  };

  // --- REGISTER ---
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!establishmentId) {
      setError("Please select your organization.");
      return;
    }
    if (!email && !mobile) {
      setError("Please enter your email address or mobile number.");
      return;
    }
    setIsLoading(true);
    try {
      const body = { username, email: email || null, mobile: mobile || null, password, role: "Member", establishment_id: parseInt(establishmentId) };
      const res = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.status === "PENDING_APPROVAL") {
          setMessage(
            "Your registration request has been sent to the organization manager for approval. " +
            "You will be able to log in once your request is approved."
          );
        } else {
          setMessage("Account created successfully. You can now log in.");
        }
        setStage("login");
      } else {
        const detail = data.detail;
        const errMsg = Array.isArray(detail)
          ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
          : (typeof detail === 'string' ? detail : data.message || "Registration failed");
        setError(errMsg);
      }
    } catch {
      setError("Network or server error during registration");
    } finally {
      setIsLoading(false);
    }
  };

  // --- FORGOT PASSWORD ---
  const handleForgot = async (e) => {
    e.preventDefault();
    setError(""); setMessage("");
    if (!email) { setError("Please enter your email address."); return; }
    setIsLoading(true);
    try {
      await fetch(`${API_BASE}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setMessage("A 6-digit reset code has been sent to your email.");
      setStage("reset");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- RESET PASSWORD ---
  const handleReset = async (e) => {
    e.preventDefault();
    setError(""); setMessage("");
    if (!email || !verificationCode || !newPassword) {
      setError("Please fill in all fields."); return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: verificationCode, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Password reset successful. You can now log in.");
        setStage("login");
      } else {
        const raw = data.detail;
        setError(Array.isArray(raw) ? raw.map(e => e.msg || JSON.stringify(e)).join('; ') : (raw || "Reset failed. Please try again."));
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (stage === "login")    return handleLogin(e);
    if (stage === "register") return handleRegister(e);
    if (stage === "forgot")   return handleForgot(e);
    if (stage === "reset")    return handleReset(e);
  };

  const goToStage = (s) => {
    setError(""); setMessage("");
    setStage(s);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--theme-background, #f8f9fa)' }}>
      {/* HEADER */}
      <header className="bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
              style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: 'none', outline: 'none', boxShadow: 'none' }}>
              HR
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold" style={{ color: 'var(--theme-text)' }}>PayrollGen</span>
              <span style={{ fontSize: '10px', color: '#78716c', letterSpacing: '0.03em', lineHeight: 1.2 }}>An Infra Delta Solutions Company</span>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors">
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* MAIN */}
      <div className="flex-1 flex">
        {/* LEFT — video panel */}
        <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center relative overflow-hidden"
          style={{ borderRadius: "16px", margin: "32px 24px 32px 32px", background: "#0a2e1a" }}>

          {/* Background video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover",
            }}
          >
            <source src="/payroll-bg.mp4" type="video/mp4" />
          </video>


        </div>

        {/* RIGHT */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 md:p-16" style={{ background: 'var(--theme-surface, #ffffff)' }}>
          <div className="w-full max-w-sm">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {stage === "login"    ? "Sign In"
               : stage === "register" ? "Create Account"
               : stage === "forgot"   ? "Forgot Password"
               : "Reset Password"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4 mt-6">

              {/* USERNAME */}
              {(stage === "login" || stage === "register") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {stage === "login" ? "Username or Email" : "Username"}
                  </label>
                  <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                    disabled={isLoading}
                    placeholder={stage === "login" ? "Enter username or email" : "Choose a username"}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}

              {/* EMAIL */}
              {(stage === "register" || stage === "forgot" || stage === "reset") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email{stage === "register" && <span className="text-gray-400 font-normal"> (or use mobile below)</span>}
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    disabled={isLoading} placeholder="Enter your email"
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}

              {/* MOBILE — register only */}
              {stage === "register" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)}
                    disabled={isLoading} placeholder="Enter your mobile number"
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}

              {/* VERIFICATION CODE — reset only */}
              {stage === "reset" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                  <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)}
                    disabled={isLoading} placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none tracking-widest text-center text-lg font-mono" />
                </div>
              )}

              {/* PASSWORD */}
              {(stage === "login" || stage === "register" || stage === "reset") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{stage === "reset" ? "New Password" : "Password"}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={stage === "reset" ? newPassword : password}
                      onChange={e => stage === "reset" ? setNewPassword(e.target.value) : setPassword(e.target.value)}
                      disabled={isLoading} placeholder="Enter password"
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none pr-10" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              {/* ESTABLISHMENT — register only */}
              {stage === "register" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                  <div className="relative">
                    <select value={establishmentId}
                      onChange={e => setEstablishmentId(e.target.value)}
                      disabled={isLoading || estLoading}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none appearance-none pr-10"
                      style={{ background: "var(--theme-surface)" }}>
                      <option value="">
                        {estLoading ? "Loading organizations…" : "Select your organization"}
                      </option>
                      {establishments.map(est => (
                        <option key={est.id} value={est.id}>{est.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    If your email matches an active employee record you'll be approved instantly, otherwise your manager will review your request.
                  </p>
                </div>
              )}

              {/* ERROR / MESSAGE */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}
              {message && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm">{message}</div>
              )}

              <button type="submit" disabled={isLoading}
                className="w-full py-2.5 px-4 rounded-lg font-medium text-white transition-all"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
                {isLoading ? "Processing…"
                  : stage === "login"    ? "Sign In"
                  : stage === "register" ? "Request Account"
                  : stage === "forgot"   ? "Send Code"
                  : "Reset Password"}
              </button>
            </form>

            {/* FOOTER LINKS */}
            <div className="mt-6 text-center">
              {stage === "login" && (
                <>
                  <button onClick={() => goToStage("forgot")}
                    className="text-sm text-emerald-600 hover:text-emerald-700 block w-full">
                    Forgot password?
                  </button>
                  <button onClick={() => goToStage("register")}
                    className="text-sm text-emerald-600 hover:text-emerald-700 mt-2 block w-full">
                    Don't have an account? Create one
                  </button>
                </>
              )}
              {stage !== "login" && (
                <button onClick={() => goToStage("login")}
                  className="text-sm text-emerald-600 hover:text-emerald-700 block w-full">
                  Back to Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
